import { TOOLTIP_MAX_MEMBERS } from './aggregate-tooltip';
import { LABEL_FONT_SIZE, LABEL_OFFSET_X } from './DependencyMapNode';
import { ResolvedDependencyMapConfig } from './dependency-map-config';
import { getMeasureCtx } from './measure-text-ctx';
import { truncateToFit } from './truncate-to-fit';
import { DependencyLink, DependencyNode } from './types';

// Subtracted from the per-column label budget so truncated labels don't
// quite touch the next column.
const TRUNCATION_BUFFER_PX = 12;

type LayoutConfig = ResolvedDependencyMapConfig['layout'];

export interface ClusterLayout {
  nodes: DependencyNode[];
  links: DependencyLink[];
  /** Pre-aggregation links, kept so `layoutFocusedSubset` can rewrite a focused
   * service's collapsed neighbors back to their aggregate. */
  rawLinks: DependencyLink[];
  /** Aggregate id → member ids (descending traffic order). Empty when no collapse occurred.
   * Used by `layoutFocusedSubset` to build the member→aggregate reverse-index. */
  aggregateMembers: Map<string, string[]>;
}

/**
 * Single-cluster layout pipeline:
 *   1. Collapse low-traffic excess per column into "Other (N)" aggregates.
 *   2. Run the layered layout on the rewritten link set.
 *   3. Decorate nodes with display labels and aggregate tooltips.
 *
 * Parallels from the rewrite bundle into `members` (not reduced — no reducer
 * is correct across additive vs. ratio metrics).
 */
export const buildClusterLayout = (inputLinks: DependencyLink[], layout: LayoutConfig): ClusterLayout => {
  const { links, labels, members } = collapseExcessPerLayer(inputLinks, {
    maxPerColumn: layout.maxNodesPerColumn,
  });
  const rawNodes = buildNodesFromLinks(links, layout, { pinToBottom: new Set(labels.keys()) });

  const aggregates = new Map<string, AggregateInfo>();
  labels.forEach((label, id) => {
    aggregates.set(id, { label, title: formatMembersTooltip(members.get(id) ?? []) });
  });

  return {
    nodes: decorateNodeLabels(rawNodes, aggregates, layout),
    links,
    rawLinks: inputLinks,
    aggregateMembers: members,
  };
};

/**
 * Re-runs the layered layout on the focused service's 1-hop neighborhood (a
 * fresh compact layout, not a filter of the full-graph positions). Shows the
 * focused service plus its raw 1-hop neighbors; neighbors collapsed in the
 * full view rewrite back to their aggregate, and parallels bundle into
 * `members` (same as `buildClusterLayout`).
 *
 * Aggregate ids aren't valid focus targets — `DependencyMapCluster` ignores
 * clicks on them and `NodeFocusSelect` only emits real ids. Passing one in
 * here yields an empty subset (no raw link has an aggregate id as endpoint).
 */
export const layoutFocusedSubset = (
  fullNodes: DependencyNode[],
  rawLinks: DependencyLink[],
  aggregateMembers: Map<string, string[]>,
  focusedId: string,
  layout: LayoutConfig
): { nodes: DependencyNode[]; links: DependencyLink[] } => {
  // Reverse index for rewriting collapsed neighbors back to their aggregate.
  const memberToAggregate = new Map<string, string>();
  aggregateMembers.forEach((members, aggId) => {
    members.forEach((m) => memberToAggregate.set(m, aggId));
  });

  const visibleIdFor = (rawId: string): string => {
    if (rawId === focusedId) return rawId;

    return memberToAggregate.get(rawId) ?? rawId;
  };

  const rewritten: RewrittenLink[] = [];
  rawLinks.forEach((link) => {
    if (link.from !== focusedId && link.to !== focusedId) return;

    const from = visibleIdFor(link.from);
    const to = visibleIdFor(link.to);

    if (from === to) return;

    rewritten.push({ from, to, raw: link });
  });
  const subsetLinks = mergeRewrittenLinks(rewritten);

  // Surviving aggregates keep their label, tooltip, and bottom-pin.
  const aggregates = new Map<string, AggregateInfo>();
  const pinToBottom = new Set<string>();
  fullNodes.forEach((node) => {
    if (!node.isAggregate) return;

    pinToBottom.add(node.id);
    aggregates.set(node.id, { label: node.label ?? node.id, title: node.title ?? '' });
  });

  const rawNodes = buildNodesFromLinks(subsetLinks, layout, { pinToBottom });

  return { nodes: decorateNodeLabels(rawNodes, aggregates, layout), links: subsetLinks };
};

interface AggregateInfo {
  /** Displayed label, e.g. "Other (5)". */
  label: string;
  /** Tooltip text (formatted member list). */
  title: string;
}

/** Newline-joined member list, capped at `TOOLTIP_MAX_MEMBERS` with a "…and N more" footer. */
const formatMembersTooltip = (members: string[]): string => {
  if (members.length === 0) return '';
  if (members.length <= TOOLTIP_MAX_MEMBERS) return members.join('\n');
  const head = members.slice(0, TOOLTIP_MAX_MEMBERS);
  return [...head, `…and ${members.length - TOOLTIP_MAX_MEMBERS} more`].join('\n');
};

/**
 * Attaches display labels. Aggregates get their precomputed label + member
 * tooltip; real ids truncate to the per-column budget (rightmost column can
 * spill into right padding) and stash the full id on `title`.
 */
const decorateNodeLabels = (
  rawNodes: DependencyNode[],
  aggregates: Map<string, AggregateInfo>,
  layout: LayoutConfig
): DependencyNode[] => {
  const maxNodeX = rawNodes.length === 0 ? 0 : Math.max(...rawNodes.map((n) => n.x));
  const truncationBudget = layout.columnSpacingPx - LABEL_OFFSET_X - TRUNCATION_BUFFER_PX;
  const ctx = getMeasureCtx();
  if (ctx) ctx.font = `${LABEL_FONT_SIZE}px sans-serif`;

  return rawNodes.map((node) => {
    const aggregate = aggregates.get(node.id);
    if (aggregate) {
      // "Other (N)" labels are short and never need truncation in practice.
      return { ...node, label: aggregate.label, title: aggregate.title, isAggregate: true };
    }
    const fullLabel = node.id;
    const shouldTruncate = ctx !== null && node.x !== maxNodeX;
    const displayLabel = shouldTruncate ? truncateToFit(fullLabel, ctx, truncationBudget) : fullLabel;
    if (displayLabel === fullLabel) {
      return { ...node, label: fullLabel };
    }
    return { ...node, label: displayLabel, title: fullLabel };
  });
};

interface CollapseOptions {
  /** Cap on rendered nodes per layer. Excess collapses into one "Other (N)" node. */
  maxPerColumn: number;
}

interface CollapseResult {
  /** Links remapped onto aggregate endpoints; self-loops dropped, parallels merged. */
  links: DependencyLink[];
  /** Display labels for aggregate ids only; real nodes render under their own id. */
  labels: Map<string, string>;
  /** Aggregate id → member ids in descending traffic order. */
  members: Map<string, string[]>;
}

// Prefix unlikely to collide with a real display name.
const OTHER_ID_PREFIX = '@@other:';
const otherIdForLayer = (layerIndex: number): string => `${OTHER_ID_PREFIX}${layerIndex}`;

/**
 * Caps each layer at `maxPerColumn` by collapsing the lowest-traffic excess
 * into a single "Other (N)" per layer. Layering matches `buildNodesFromLinks`
 * so re-layering the rewritten links reproduces the capped layers.
 */
const collapseExcessPerLayer = (links: DependencyLink[], { maxPerColumn }: CollapseOptions): CollapseResult => {
  const depths = computeDepths(links);

  const layers = new Map<number, string[]>();
  depths.forEach((d, id) => {
    if (!layers.has(d)) layers.set(d, []);
    layers.get(d)!.push(id);
  });

  // Each link counts toward both endpoints; high-traffic floats to the top.
  // Self-loops contribute once (both endpoints are the same node) — adding
  // twice would inflate that node's score and skew the per-layer collapse.
  const traffic = new Map<string, number>();
  depths.forEach((_, id) => traffic.set(id, 0));
  links.forEach(({ from, to, value }) => {
    traffic.set(from, (traffic.get(from) ?? 0) + value);
    if (from === to) return;
    traffic.set(to, (traffic.get(to) ?? 0) + value);
  });

  const remap = new Map<string, string>();
  const labels = new Map<string, string>();
  const members = new Map<string, string[]>();

  layers.forEach((layer, d) => {
    if (layer.length <= maxPerColumn) return;
    const sorted = [...layer].sort((a, b) => {
      const diff = (traffic.get(b) ?? 0) - (traffic.get(a) ?? 0);

      if (diff !== 0) return diff;

      // Alphabetical tie-break for deterministic output (Playwright snapshots).
      return a.localeCompare(b);
    });

    // Keep top `cap - 1` so visible nodes + the Other aggregate total `cap`.
    const collapsed = sorted.slice(maxPerColumn - 1);
    const otherId = otherIdForLayer(d);
    collapsed.forEach((id) => remap.set(id, otherId));
    labels.set(otherId, `Other (${collapsed.length})`);
    members.set(otherId, collapsed);
  });

  if (remap.size === 0) return { links, labels, members };

  // Remap endpoints, drop intra-aggregate self-loops, bundle parallels (the
  // renderer needs the raw members for paired stripes + tooltips).
  const rewritten: RewrittenLink[] = [];

  links.forEach((link) => {
    const from = remap.get(link.from) ?? link.from;
    const to = remap.get(link.to) ?? link.to;
    if (from === to) return;
    rewritten.push({ from, to, raw: link });
  });

  return { links: mergeRewrittenLinks(rewritten), labels, members };
};

/**
 * Rewritten edge + its source raw link. Visible endpoints drive merging;
 * the raw link feeds `members[]` so tooltips show un-rewritten names.
 */
interface RewrittenLink {
  from: string;
  to: string;
  raw: DependencyLink;
}

/**
 * Combines parallels sharing visible endpoints. Solo pairs render as a
 * normal edge; 2+ produce an aggregate edge whose `members` carries the raw
 * links (for tooltips) and whose `value` is the member count (a diagnostic;
 * the renderer ignores it for color/width).
 *
 * Output order = first-encounter order of each endpoint pair.
 */
const mergeRewrittenLinks = (links: RewrittenLink[]): DependencyLink[] => {
  interface Group {
    from: string;
    to: string;
    members: DependencyLink[];
  }
  const groups = new Map<string, Group>();
  links.forEach(({ from, to, raw }) => {
    const key = edgeKey(from, to);
    const existing = groups.get(key);
    if (existing) {
      existing.members.push(raw);
    } else {
      groups.set(key, { from, to, members: [raw] });
    }
  });

  return [...groups.values()].map(({ from, to, members }) => {
    if (members.length === 1) {
      const sole = members[0]!;
      // No rewrite: forward untouched so reference equality holds (tests rely on this).
      if (sole.from === from && sole.to === to) return sole;
      return { from, to, value: sole.value };
    }
    return { from, to, value: members.length, members };
  });
};

/**
 * Layer assignment via back-edge-reversed Kahn's longest-path; same algorithm
 * as `buildNodesFromLinks`, factored out so collapses can run pre-layout.
 */
const computeDepths = (links: DependencyLink[]): Map<string, number> => {
  const ids: string[] = [];
  const seen = new Set<string>();
  links.forEach(({ from, to }) => {
    [from, to].forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
  });

  // Self-loops survive back-edge reversal (`A → A` reversed is `A → A`) and
  // would starve Kahn's by keeping `remaining[A] > 0` forever. Drop them
  // here; the renderer still sees them via `links`.
  const layoutLinks = links.filter(({ from, to }) => from !== to);

  const rawOutgoing = new Map<string, string[]>();
  ids.forEach((id) => rawOutgoing.set(id, []));
  layoutLinks.forEach(({ from, to }) => rawOutgoing.get(from)!.push(to));
  const backEdges = findBackEdges(ids, rawOutgoing);

  const outgoing = new Map<string, string[]>();
  const remaining = new Map<string, number>();
  ids.forEach((id) => {
    outgoing.set(id, []);
    remaining.set(id, 0);
  });
  layoutLinks.forEach(({ from, to }) => {
    const reversed = backEdges.has(edgeKey(from, to));
    const layoutFrom = reversed ? to : from;
    const layoutTo = reversed ? from : to;
    outgoing.get(layoutFrom)!.push(layoutTo);
    remaining.set(layoutTo, (remaining.get(layoutTo) ?? 0) + 1);
  });

  const depth = new Map<string, number>();
  const queue: string[] = [];
  ids.forEach((id) => {
    if (remaining.get(id) === 0) {
      depth.set(id, 0);
      queue.push(id);
    }
  });
  while (queue.length > 0) {
    const id = queue.shift()!;
    const d = depth.get(id) ?? 0;
    outgoing.get(id)!.forEach((next) => {
      depth.set(next, Math.max(depth.get(next) ?? 0, d + 1));
      const r = (remaining.get(next) ?? 0) - 1;
      remaining.set(next, r);
      if (r === 0) queue.push(next);
    });
  }

  return depth;
};

interface BuildNodesOptions {
  /** Ids pinned to the bottom of their layer (skip barycenter); anchors "Other" aggregates. */
  pinToBottom?: Set<string>;
}

/**
 * Layered (Sugiyama-style) layout:
 *   1. Collect unique ids.
 *   2. DFS-detect back-edges; treat as reversed so layering sees a DAG.
 *   3. Assign depths via Kahn's longest-path.
 *   4. Group by depth.
 *   5. One barycenter pass for crossing reduction; pinned ids skip the sort
 *      and append at the layer bottom.
 *   6. depth → x, position-in-layer → y, centered on the origin.
 *
 * Back-edges still render in their original direction.
 */
const buildNodesFromLinks = (
  links: DependencyLink[],
  layout: LayoutConfig,
  options: BuildNodesOptions = {}
): DependencyNode[] => {
  const { pinToBottom } = options;
  const ids: string[] = [];
  const seen = new Set<string>();
  links.forEach(({ from, to }) => {
    [from, to].forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
  });

  // Same self-loop drop as `computeDepths`; renderer keeps `links` intact.
  const layoutLinks = links.filter(({ from, to }) => from !== to);

  const rawOutgoing = new Map<string, string[]>();
  ids.forEach((id) => rawOutgoing.set(id, []));
  layoutLinks.forEach(({ from, to }) => rawOutgoing.get(from)!.push(to));
  const backEdges = findBackEdges(ids, rawOutgoing);

  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  const remaining = new Map<string, number>();

  ids.forEach((id) => {
    outgoing.set(id, []);
    incoming.set(id, []);
    remaining.set(id, 0);
  });

  layoutLinks.forEach(({ from, to }) => {
    const reversed = backEdges.has(edgeKey(from, to));
    const layoutFrom = reversed ? to : from;
    const layoutTo = reversed ? from : to;
    outgoing.get(layoutFrom)!.push(layoutTo);
    incoming.get(layoutTo)!.push(layoutFrom);
    remaining.set(layoutTo, (remaining.get(layoutTo) ?? 0) + 1);
  });

  const depth = new Map<string, number>();
  const queue: string[] = [];

  ids.forEach((id) => {
    if (remaining.get(id) === 0) {
      depth.set(id, 0);
      queue.push(id);
    }
  });

  while (queue.length > 0) {
    const id = queue.shift()!;
    const d = depth.get(id) ?? 0;
    outgoing.get(id)!.forEach((next) => {
      depth.set(next, Math.max(depth.get(next) ?? 0, d + 1));
      const r = (remaining.get(next) ?? 0) - 1;
      remaining.set(next, r);
      if (r === 0) queue.push(next);
    });
  }

  const layers: string[][] = [];
  ids.forEach((id) => {
    const d = depth.get(id) ?? 0;
    if (!layers[d]) layers[d] = [];
    layers[d].push(id);
  });

  const orderInLayer = new Map<string, number>();
  layers.forEach((layer, d) => {
    // Only regulars sort; pinned ids append in insertion order at the bottom.
    const regular: string[] = [];
    const pinned: string[] = [];
    layer.forEach((id) => {
      if (pinToBottom?.has(id)) {
        pinned.push(id);
      } else {
        regular.push(id);
      }
    });

    if (d > 0) {
      regular.sort((a, b) => barycenter(incoming.get(a)!, orderInLayer) - barycenter(incoming.get(b)!, orderInLayer));
    }

    layer.splice(0, layer.length, ...regular, ...pinned);
    layer.forEach((id, i) => orderInLayer.set(id, i));
  });

  const maxDepth = layers.length - 1;
  const nodes: DependencyNode[] = [];
  layers.forEach((layer, d) => {
    const x = (d - maxDepth / 2) * layout.columnSpacingPx;
    layer.forEach((id, i) => {
      const y = (i - (layer.length - 1) / 2) * layout.nodeSpacingPx;
      nodes.push({ id, x, y });
    });
  });

  return nodes;
};

const edgeKey = (from: string, to: string): string => `${from}->${to}`;

/**
 * Iterative DFS with white/gray/black coloring; edges to gray nodes close
 * cycles and are marked as back-edges (greedy; min feedback arc set is NP-hard).
 */
const findBackEdges = (ids: string[], outgoing: Map<string, string[]>): Set<string> => {
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  ids.forEach((id) => color.set(id, WHITE));
  const backEdges = new Set<string>();

  // Iterative to avoid blowing the call stack on large graphs.
  ids.forEach((rootId) => {
    if (color.get(rootId) !== WHITE) return;

    const stack: Array<{ id: string; childIndex: number }> = [{ id: rootId, childIndex: 0 }];
    color.set(rootId, GRAY);

    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      // Loop guard ensures stack is non-empty.
      if (frame === undefined) break;
      const children = outgoing.get(frame.id) ?? [];
      if (frame.childIndex >= children.length) {
        color.set(frame.id, BLACK);
        stack.pop();
        continue;
      }
      const next = children[frame.childIndex++];
      // childIndex < children.length guarantees `next` is defined.
      if (next === undefined) continue;
      const c = color.get(next);
      if (c === GRAY) {
        backEdges.add(edgeKey(frame.id, next));
      } else if (c === WHITE) {
        color.set(next, GRAY);
        stack.push({ id: next, childIndex: 0 });
      }
    }
  });

  return backEdges;
};

/**
 * Mean in-layer index of a node's previous-layer neighbors — the standard
 * Sugiyama crossing-reduction heuristic (cheap, not optimal). Falls back to
 * 0 (top of layer) for neighborless nodes.
 */
const barycenter = (neighbors: string[], orderInLayer: Map<string, number>): number => {
  if (neighbors.length === 0) return 0;
  const sum = neighbors.reduce((acc, id) => acc + (orderInLayer.get(id) ?? 0), 0);
  return sum / neighbors.length;
};
